import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  useTheme,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Add, Help } from "@mui/icons-material";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import MenuLayout from "../../../app/MenuLayout";
import { UrlPathSegment } from "../../../../api/url";
import { Api } from "../../../../api/Api";
import {
  thunkCreateErrorNotification,
  thunkCreateSuccessNotification,
} from "../../../../store/actions/NotificationActions";
import FilterRow from "./components/FilterRow";
import ItemFilterModal from "./components/ItemFilterModal";
import { SegmentDefinition, ItemFilterRule, FilterConfig, EasySegmentCreateRequest } from "./types/AdHocTypes";
import { getFilterConfigs, loadFieldConfigurations } from "./config/FilterConfigs";
import ImsPaperHead from "../../../atoms/ImsPaperHead";

const AdHocSegmentsPage = (props: ThunkProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [segmentName, setSegmentName] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [filterRows, setFilterRows] = useState<Array<{ id: string; filterType: string; config: any }>>([]);
  const [itemFilterModalOpen, setItemFilterModalOpen] = useState(false);
  const [activeItemFilterElement, setActiveItemFilterElement] = useState<{
    rowId: string;
    rules: ItemFilterRule[];
    onSave: (rules: ItemFilterRule[]) => void;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // API-driven state
  const [filterConfigs, setFilterConfigs] = useState<FilterConfig[]>([]);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [configsError, setConfigsError] = useState<string | null>(null);

  // Load field configurations on component mount
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        setIsLoadingConfigs(true);
        setConfigsError(null);

        // Load field configurations from API
        await loadFieldConfigurations();

        // Get the constructed filter configs
        const configs = await getFilterConfigs();
        setFilterConfigs(configs);
      } catch (error) {
        console.error("Failed to load field configurations:", error);
        setConfigsError("Failed to load field configurations. Using fallback data.");

        // Try to get cached configs as fallback
        try {
          const configs = await getFilterConfigs();
          setFilterConfigs(configs);
        } catch (fallbackError) {
          console.error("Fallback also failed:", fallbackError);
          setFilterConfigs([]);
        }
      } finally {
        setIsLoadingConfigs(false);
      }
    };

    loadConfigs();
  }, []);

  const addFilterRow = () => {
    const newRow = {
      id: `filter-${Date.now()}`,
      filterType: "",
      config: {},
    };
    setFilterRows([...filterRows, newRow]);
  };

  const removeFilterRow = (id: string) => {
    setFilterRows(filterRows.filter((row) => row.id !== id));
  };

  const updateFilterRow = (id: string, updates: Partial<(typeof filterRows)[0]>) => {
    setFilterRows(filterRows.map((row) => (row.id === id ? { ...row, ...updates } : row)));
  };

  const openItemFilterModal = (
    rowId: string,
    currentRules: ItemFilterRule[],
    onSave: (rules: ItemFilterRule[]) => void
  ) => {
    setActiveItemFilterElement({
      rowId,
      rules: currentRules,
      onSave,
    });
    setItemFilterModalOpen(true);
  };

  const closeItemFilterModal = () => {
    setItemFilterModalOpen(false);
    setActiveItemFilterElement(null);
  };

  const saveItemFilterModal = (rules: ItemFilterRule[]) => {
    if (activeItemFilterElement) {
      activeItemFilterElement.onSave(rules);
    }
    closeItemFilterModal();
  };

  const generateSegmentId = (name: string): string => {
    return name
      .normalize("NFD")
      .replaceAll(/[\u0300-\u036f]/g, "")
      .replaceAll(/[^\w\s]/g, "")
      .replaceAll(/\s+/g, "")
      .toLowerCase();
  };

  // Check if any filter row has validation errors (for "between" operators)
  const hasValidationErrors = (): boolean => {
    return filterRows.some((row) => {
      const config = filterConfigs.find((f) => f.id === row.filterType);
      if (!config) return false;

      // Check for "between" operators
      const isBetweenOperator = row.config.operator && row.config.operator.toLowerCase().includes("between");
      if (!isBetweenOperator) return false;

      // Check if it's a numeric field
      const isNumericField = config.dataType === "NUMBER" || config.dataType === "DECIMAL" || config.dataType === "AGE";
      if (!isNumericField) return false;

      // Parse values
      const values = row.config.value
        ? String(row.config.value)
            .split(",")
            .map((v: string) => v.trim())
        : ["", ""];
      const fromValue = values[0] || "";
      const toValue = values[1] || "";

      // Validate: "from" should not be greater than "to"
      if (fromValue && toValue && Number.parseFloat(fromValue) > Number.parseFloat(toValue)) {
        return true; // Has error
      }

      return false;
    });
  };

  // Check if all filter rows are fully configured
  const areAllFiltersComplete = (): boolean => {
    return filterRows.every((row) => {
      // Must have a filter type selected
      if (!row.filterType) return false;

      const config = filterConfigs.find((f) => f.id === row.filterType);
      if (!config) return false;

      // Must have an operator selected
      if (!row.config.operator) return false;

      // For boolean fields with "Is true" or "Is false" operators, no value is needed
      if (
        config.dataType === "BOOLEAN" &&
        ["Is true", "Is false"].includes(row.config.operator)
      ) {
        return true;
      }

      // For modal-item-filter, check if rules are defined
      if (config.inputType === "modal-item-filter") {
        return row.config.itemRules && row.config.itemRules.length > 0;
      }

      // For all other cases, must have a value
      return row.config.value !== undefined && row.config.value !== "";
    });
  };

  const generateSegmentDefinition = (): SegmentDefinition => {
    const rules = filterRows
      .filter((row) => {
        if (!row.filterType) return false;

        const config = filterConfigs.find((f) => f.id === row.filterType);
        if (!config) return false;

        // For boolean fields with "Is true" or "Is false" operators, only operator is needed
        if (
          config.dataType === "BOOLEAN" &&
          row.config.operator &&
          ["Is true", "Is false"].includes(row.config.operator)
        ) {
          return true;
        }

        // For other fields, require both operator and value
        return (
          Object.keys(row.config).length > 0 &&
          row.config.operator &&
          row.config.value !== undefined &&
          row.config.value !== ""
        );
      })
      .map((row) => {
        const config = filterConfigs.find((f) => f.id === row.filterType);
        if (!config) return null;

        let ruleValue = row.config.value;
        let ruleOperator = row.config.operator || "";

        // Handle boolean operators that don't need explicit values
        if (config.dataType === "BOOLEAN" && row.config.operator) {
          if (row.config.operator === "Is true") {
            ruleValue = true;
            ruleOperator = "IS_TRUE";
          } else if (row.config.operator === "Is false") {
            ruleValue = false;
            ruleOperator = "IS_FALSE";
          }
        }

        const rule: any = {
          filter: row.filterType,
          operator: ruleOperator,
          value: ruleValue,
        };

        // Add API-specific fields for customer field configs
        if (config.fieldId && config.fieldName) {
          rule.fieldId = config.fieldId;
          rule.fieldName = config.fieldName;

          // Convert operator description back to operator type (if not already converted for boolean)
          if (config.dataType !== "BOOLEAN" || (ruleOperator !== "IS_TRUE" && ruleOperator !== "IS_FALSE")) {
            const selectedOperator = config.availableOperators?.find((op) => op.description === row.config.operator);
            if (selectedOperator) {
              rule.operator = selectedOperator.operatorType;
            }
          }
        }

        // Add additional fields based on filter type
        if (config.inputType === "dynamic-options") {
          rule.question_id = row.config.questionId;
        }

        return rule;
      })
      .filter((rule) => rule !== null);

    return {
      name: segmentName || "Untitled Segment",
      rules,
    };
  };

  const handleSaveSegment = async () => {
    setIsSaving(true);

    try {
      const definition = generateSegmentDefinition();

      // Convert the segment definition to the EasySegmentCreateRequest format
      const easySegmentRequest: EasySegmentCreateRequest = {
        name: definition.name,
        rules: definition.rules.map((rule: any) => ({
          fieldName: rule.fieldName || rule.filter,
          operatorType: rule.operator,
          expectedValue: String(rule.value),
        })),
      };

      // Auto-generate segment ID if not provided (same logic as custom segment)
      const isBlank = (value?: string) => !value || (typeof value === "string" && !value.trim());
      if (isBlank(segmentId) && !isBlank(segmentName)) {
        easySegmentRequest.externalId = generateSegmentId(segmentName);
      } else if (!isBlank(segmentId)) {
        easySegmentRequest.externalId = segmentId;
      }

      // Call the createEasySegment API
      await Api.createEasySegment(easySegmentRequest);

      // Show success notification
      props.thunkCreateSuccessNotification(t("segmentsForm.segmentCreated"));

      // Navigate back to segments list
      navigate(UrlPathSegment);
    } catch (error: any) {
      console.error("Error saving segment:", error);
      props.thunkCreateErrorNotification(error.message || t("segmentsForm.segmentSaveError"), error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MenuLayout headline={t("adHocSegments.title")}>
      <Paper sx={{ p: theme.spacing(2), mb: theme.spacing(2), marginTop: theme.spacing(4) }}>
        <Box sx={{ p: theme.spacing(2) }}>
          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <ImsPaperHead text={t("adHocSegments.basicInfo")} />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    placeholder={t("adHocSegments.segmentNamePlaceholder")}
                    value={segmentName}
                    onChange={(e) => setSegmentName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    label={t("adHocSegments.segmentName")}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    placeholder={t("segmentBuilder.csv.step1.segmentId")}
                    value={segmentId}
                    onChange={(e) => setSegmentId(e.target.value)}
                    fullWidth
                    variant="outlined"
                    label={t("segmentBuilder.csv.step1.segmentId")}
                  />
                  <Tooltip title={t("segmentBuilder.csv.step1.segmentIdInfo")} placement="right-start">
                    <Help color="primary" sx={{ cursor: "pointer" }} />
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>

            {/* Filter Rules Section */}
            <Grid item xs={12} sx={{ mt: 6 }}>
              <Box sx={{ clear: "both", display: "flex", alignItems: "center", gap: 2 }}>
                <ImsPaperHead text={t("adHocSegments.filterRules")} />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(2), clear: "both"}}>
                {isLoadingConfigs ? (
                  <Box
                    sx={{
                      display: "flex", 
                      alignItems: "center",
                      justifyContent: "center",
                      padding: theme.spacing(4),
                    }}
                  >
                    <CircularProgress sx={{ marginRight: theme.spacing(2) }} />
                    <Typography variant="body1" color="text.secondary">
                      Loading field configurations...
                    </Typography>
                  </Box>
                ) : configsError ? (
                  <Box sx={{ padding: theme.spacing(3), textAlign: "center" }}>
                    <Typography variant="body1" color="error.main" sx={{ marginBottom: theme.spacing(2) }}>
                      ⚠️ {configsError}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Some features may not work properly. Please check your connection and refresh the page.
                    </Typography>
                  </Box>
                ) : (
                  filterRows.map((row) => (
                    <Box key={row.id}>
                      <FilterRow
                        id={row.id}
                        filterType={row.filterType}
                        config={row.config}
                        filterConfigs={filterConfigs}
                        onRemove={() => removeFilterRow(row.id)}
                        onUpdate={(updates) => updateFilterRow(row.id, updates)}
                        onOpenItemFilter={openItemFilterModal}
                      />
                    </Box>
                  ))
                )}
              </Box>

              <Button
                startIcon={<Add />}
                variant="contained"
                color="primary"
                onClick={addFilterRow}
                disabled={isLoadingConfigs || filterConfigs.length === 0}
                style={{ minWidth: 150 }}
                sx={{ mt: 3 }}
              >
                {t("adHocSegments.addFilter")}
              </Button>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: theme.spacing(2) }}>
                <Button
                  component={Link}
                  to={UrlPathSegment}
                  color="secondary"
                  variant="contained"
                  sx={{ mt: 4 }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveSegment}
                  disabled={
                    !segmentName.trim() ||
                    filterRows.length === 0 ||
                    !areAllFiltersComplete() ||
                    isLoadingConfigs ||
                    isSaving ||
                    hasValidationErrors()
                  }
                  sx={{
                    mt: 4,
                    minWidth: 200,
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={20} sx={{ marginRight: 1, color: "white" }} />
                      {t("adHocSegments.saving")}
                    </>
                  ) : (
                    t("adHocSegments.saveSegment")
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Item Filter Modal */}
      <ItemFilterModal
        open={itemFilterModalOpen}
        onClose={closeItemFilterModal}
        onSave={saveItemFilterModal}
        initialRules={activeItemFilterElement?.rules || []}
      />
    </MenuLayout>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators(
    {
      thunkCreateSuccessNotification,
      thunkCreateErrorNotification,
    },
    dispatch
  );
};

const connector = connect(null, mapDispatchToProps);

type ThunkProps = ConnectedProps<typeof connector>;

export default connector(AdHocSegmentsPage);
